/**
 * Ready-made order-confirmation templates for the thank-you page (`thank_you.page_html`).
 *
 * Each entry is the PM-authored HTML with its static copy replaced by `{{key}}` placeholders,
 * plus one string table per locale. `buildConfirmationTemplate` resolves the placeholders and
 * hands back finished HTML that is inserted into the merchant's editor, where they own it.
 *
 * Two brace flavours live in this file and must not be confused:
 *  - `{{key}}`  — a localized string, resolved HERE and never seen by the merchant.
 *  - `{token}`  — a storefront shortcode, left intact for `thankYouTokens` to fill per order.
 *
 * The localized values carry their own `<strong>` markup: the emphasis is part of the design,
 * so stripping it to keep the strings "pure copy" would flatten every template.
 *
 * The legacy bundle writes `page_html` into a shadow root via `innerHTML`; the NubeSDK path
 * rebuilds it as components (`form-tree/src/htmlTree.ts`), which keeps the cards, inline styles
 * and SVG icons. `htmlToMarkdown` is only the fallback for markup that converter cannot read.
 * `confirmationTemplates.test.ts` asserts every design stays inside its vocabulary.
 *
 * 🚨 **Both paths reflow the page to the form's width, and a template must survive that.** The
 * form is 460px at its widest, less its own padding — so a design that only works at its declared
 * width is a design a buyer never sees intact. See `.cod-thankyou-page table` in
 * `form-renderer/src/styles.ts` and `tableNode` / `FIT` in `htmlTree.ts`.
 */

export type ConfirmationTemplateId =
  | '1-clean-minimal'
  | '2-bold-banner'
  | '3-receipt'
  | '4-soft-friendly'
  | '5-delivery-steps';

/** Locales the template copy ships in. `pt-BR` is Nuvemshop Brazil; `en` is the fallback. */
export type TemplateLocale = 'en' | 'es' | 'pt-BR';

const TEMPLATE_LOCALES: TemplateLocale[] = ['en', 'es', 'pt-BR'];

interface ConfirmationTemplate {
  html: string;
  /**
   * The placeholder holding the "call to confirm" line, whose value comes from
   * `<key>_call_on` / `<key>_call_off` depending on the toggle. Absent on template 5, which
   * removes a whole timeline step instead.
   */
  callKey?: string;
  strings: Record<TemplateLocale, Record<string, string>>;
}

/** Copy every template draws on, so the four shared keys are written once per locale. */
const SHARED: Record<TemplateLocale, Record<string, string>> = {
  en: {
    order_confirmed: 'Order confirmed',
    order_number: 'Order {order_number}',
    shipping: 'Shipping',
    total: 'Total',
  },
  es: {
    order_confirmed: 'Pedido confirmado',
    order_number: 'Pedido {order_number}',
    shipping: 'Envío',
    total: 'Total',
  },
  'pt-BR': {
    order_confirmed: 'Pedido confirmado',
    order_number: 'Pedido {order_number}',
    shipping: 'Frete',
    total: 'Total',
  },
};

const CLEAN_MINIMAL = `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;max-width:480px;margin:0 auto;background:#ffffff;border:1px solid #E5E7EB;border-radius:14px;overflow:hidden;box-shadow:0 8px 30px rgba(0,0,0,0.08);">

  <!-- Header -->
  <div style="padding:32px 24px 20px;text-align:center;">
    <div style="width:60px;height:60px;margin:0 auto 16px;background:#ECFDF5;border-radius:50%;display:flex;align-items:center;justify-content:center;">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12.5L9.5 18L20 6.5" stroke="#16A34A" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <h2 style="margin:0;color:#111827;font-size:21px;font-weight:700;">{{order_confirmed}}</h2>
    <p style="margin:8px 0 0;color:#6B7280;font-size:14px;">{{order_number}}</p>
  </div>

  <!-- Message -->
  <div style="padding:0 24px;text-align:center;">
    <p style="margin:0;color:#374151;font-size:14px;line-height:1.6;">
      {{greeting}}
    </p>
  </div>

  <!-- Order details -->
  <div style="margin:20px 24px 0;border-top:1px solid #E5E7EB;padding-top:16px;">
    <p style="margin:0 0 6px;color:#6B7280;font-size:12px;font-weight:600;letter-spacing:0.6px;text-transform:uppercase;">{{your_order}}</p>
    <p style="margin:0;color:#111827;font-size:14px;line-height:1.6;">{products_summary_with_quantity}</p>
    <div style="display:flex;justify-content:space-between;margin-top:12px;">
      <span style="color:#6B7280;font-size:13px;">{{shipping}}</span>
      <span style="color:#111827;font-size:13px;">{shipping_rate_name}</span>
    </div>
    <div style="display:flex;justify-content:space-between;margin-top:8px;padding-top:10px;border-top:1px solid #E5E7EB;">
      <span style="color:#111827;font-size:15px;font-weight:700;">{{total}}</span>
      <span style="color:#111827;font-size:15px;font-weight:700;">{order_total}</span>
    </div>
  </div>

  <!-- Delivery details -->
  <div style="margin:16px 24px 0;border-top:1px solid #E5E7EB;padding-top:16px;">
    <p style="margin:0 0 6px;color:#6B7280;font-size:12px;font-weight:600;letter-spacing:0.6px;text-transform:uppercase;">{{delivery_address}}</p>
    <p style="margin:0;color:#111827;font-size:14px;line-height:1.7;">
      <strong>{full_name}</strong><br>
      {address}<br>
      {city}, {province}
    </p>
  </div>

  <!-- COD note -->
  <div style="margin:20px 24px 24px;background:#F9FAFB;border-radius:10px;padding:12px 16px;text-align:center;">
    <span style="color:#374151;font-size:13px;line-height:1.5;">
      {{cod_note}}
    </span>
  </div>
</div>`;

const BOLD_BANNER = `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;max-width:480px;margin:0 auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 12px 40px rgba(17,24,39,0.22);">

  <!-- Header -->
  <div style="background:linear-gradient(135deg,#111827 0%,#1F2937 60%,#374151 100%);padding:28px 24px 24px;text-align:center;">
    <div style="width:56px;height:56px;margin:0 auto 14px;background:#ffffff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(0,0,0,0.25);">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12.5L9.5 18L20 6.5" stroke="#F59E0B" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <h2 style="margin:0;color:#ffffff;font-size:21px;font-weight:700;letter-spacing:1px;">{{header}}</h2>
    <div style="display:inline-block;margin-top:10px;background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.35);border-radius:999px;padding:5px 16px;color:#ffffff;font-size:13px;font-weight:600;letter-spacing:0.5px;">
      {{order_number}}
    </div>
  </div>

  <!-- Greeting -->
  <div style="padding:22px 24px 0;text-align:center;">
    <p style="margin:0;color:#111827;font-size:15px;line-height:1.6;">
      {{greeting}}
    </p>
  </div>

  <!-- Order summary card -->
  <div style="margin:18px 24px 0;background:#F9FAFB;border:1px solid #E5E7EB;border-radius:12px;overflow:hidden;">
    <div style="padding:12px 16px 10px;border-bottom:1px solid #E5E7EB;display:flex;align-items:center;gap:8px;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;">
        <path d="M12 2L3 6.5v11L12 22l9-4.5v-11L12 2z" stroke="#111827" stroke-width="1.8" stroke-linejoin="round"/>
        <path d="M3 6.5L12 11l9-4.5M12 11v11" stroke="#111827" stroke-width="1.8" stroke-linejoin="round"/>
      </svg>
      <span style="color:#111827;font-size:12px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;">{{your_order}}</span>
    </div>
    <div style="padding:12px 16px;">
      <p style="margin:0;color:#111827;font-size:13.5px;line-height:1.6;">{products_summary_with_quantity}</p>
      <div style="display:flex;justify-content:space-between;margin-top:10px;padding-top:10px;border-top:1px dashed #D1D5DB;">
        <span style="color:#6B7280;font-size:12.5px;">{{shipping_line}}</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
        <span style="color:#111827;font-size:14px;font-weight:600;">{{total_to_pay}}</span>
        <span style="color:#111827;font-size:18px;font-weight:700;">{order_total}</span>
      </div>
    </div>
  </div>

  <!-- Delivery details card -->
  <div style="margin:14px 24px 0;background:#F9FAFB;border:1px solid #E5E7EB;border-radius:12px;overflow:hidden;">
    <div style="padding:12px 16px 10px;border-bottom:1px solid #E5E7EB;display:flex;align-items:center;gap:8px;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;">
        <path d="M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11z" stroke="#111827" stroke-width="1.8" stroke-linejoin="round"/>
        <circle cx="12" cy="10" r="2.6" stroke="#111827" stroke-width="1.8"/>
      </svg>
      <span style="color:#111827;font-size:12px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;">{{delivery_details}}</span>
    </div>
    <div style="padding:12px 16px;">
      <p style="margin:0;color:#111827;font-size:13.5px;line-height:1.7;">
        <strong>{full_name}</strong><br>
        {address}<br>
        {city}, {province}
      </p>
    </div>
  </div>

  <!-- COD notice -->
  <div style="margin:14px 24px 22px;background:#FFFBEB;border:1px solid #F59E0B;border-radius:10px;padding:14px 16px;display:flex;align-items:center;gap:12px;text-align:left;">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;">
      <rect x="2" y="7" width="14" height="10" rx="2" stroke="#B45309" stroke-width="1.8"/>
      <path d="M16 10h3.2L22 13v4h-6" stroke="#B45309" stroke-width="1.8" stroke-linejoin="round"/>
      <circle cx="7" cy="17" r="1.8" fill="#ffffff" stroke="#B45309" stroke-width="1.8"/>
      <circle cx="18" cy="17" r="1.8" fill="#ffffff" stroke="#B45309" stroke-width="1.8"/>
    </svg>
    <span style="color:#111827;font-size:13px;line-height:1.5;">
      {{cod_note}}
    </span>
  </div>

  <!-- Footer strip -->
  <div style="background:#F3F4F6;padding:12px 24px;text-align:center;">
    <span style="color:#6B7280;font-size:12px;">{{footer}}</span>
  </div>
</div>`;

const RECEIPT = `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;max-width:420px;margin:0 auto;background:#ffffff;border-radius:4px;overflow:hidden;box-shadow:0 10px 34px rgba(0,0,0,0.14);border:1px solid #E5E7EB;">

  <!-- Header -->
  <div style="padding:26px 24px 18px;text-align:center;border-bottom:2px dashed #D1D5DB;">
    <div style="width:52px;height:52px;margin:0 auto 12px;border:2px solid #111827;border-radius:50%;display:flex;align-items:center;justify-content:center;">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12.5L9.5 18L20 6.5" stroke="#111827" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <h2 style="margin:0;color:#111827;font-size:18px;font-weight:700;letter-spacing:2px;text-transform:uppercase;">{{header}}</h2>
    <p style="margin:6px 0 0;color:#6B7280;font-size:13px;font-family:'Courier New',monospace;">{{order_no}}</p>
  </div>

  <!-- Items -->
  <div style="padding:16px 24px 0;">
    <p style="margin:0 0 4px;color:#6B7280;font-size:11px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;">{{items}}</p>
    <p style="margin:0;color:#111827;font-size:13.5px;line-height:1.7;font-family:'Courier New',monospace;">{products_summary_with_quantity}</p>

    <div style="border-top:2px dashed #D1D5DB;margin-top:14px;padding-top:12px;">
      <div style="display:flex;justify-content:space-between;">
        <span style="color:#6B7280;font-size:13px;">{{shipping}}</span>
        <span style="color:#111827;font-size:13px;font-family:'Courier New',monospace;">{shipping_rate_name}</span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:10px;">
        <span style="color:#111827;font-size:15px;font-weight:700;letter-spacing:1px;text-transform:uppercase;">{{total}}</span>
        <span style="color:#111827;font-size:17px;font-weight:700;font-family:'Courier New',monospace;">{order_total}</span>
      </div>
    </div>
  </div>

  <!-- Delivery -->
  <div style="padding:14px 24px 0;">
    <div style="border-top:2px dashed #D1D5DB;padding-top:12px;">
      <p style="margin:0 0 4px;color:#6B7280;font-size:11px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;">{{deliver_to}}</p>
      <p style="margin:0;color:#111827;font-size:13.5px;line-height:1.7;">
        <strong>{full_name}</strong><br>
        {address}, {city}, {province}<br>
        {{tel}}
      </p>
    </div>
  </div>

  <!-- COD note -->
  <div style="margin:16px 24px 0;background:#111827;border-radius:6px;padding:12px 16px;text-align:center;">
    <span style="color:#ffffff;font-size:13px;letter-spacing:0.4px;">
      {{cod_bar}}
    </span>
  </div>

  <!-- Barcode strip -->
  <div style="margin:18px auto 6px;width:200px;height:36px;background:repeating-linear-gradient(90deg,#111827 0px,#111827 2px,#ffffff 2px,#ffffff 5px,#111827 5px,#111827 6px,#ffffff 6px,#ffffff 10px);"></div>
  <p style="margin:0 0 20px;text-align:center;color:#9CA3AF;font-size:11px;font-family:'Courier New',monospace;">{{footer}}</p>
</div>`;

const SOFT_FRIENDLY = `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;max-width:480px;margin:0 auto;background:#FFFDF9;border-radius:24px;overflow:hidden;box-shadow:0 10px 36px rgba(120,80,40,0.15);">

  <!-- Header -->
  <div style="background:#FDF0E7;padding:30px 24px 24px;text-align:center;">
    <div style="width:64px;height:64px;margin:0 auto 14px;background:#ffffff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(120,80,40,0.15);">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 8.5c0 5-8 11-8 11s-8-6-8-11a4.5 4.5 0 018-2.8A4.5 4.5 0 0120 8.5z" stroke="#E07B39" stroke-width="2" stroke-linejoin="round"/>
      </svg>
    </div>
    <h2 style="margin:0;color:#7C3F12;font-size:21px;font-weight:700;">{{header}}</h2>
    <p style="margin:8px 0 0;color:#A16B3F;font-size:14px;">{{subheader}}</p>
  </div>

  <!-- Message -->
  <div style="padding:20px 24px 0;text-align:center;">
    <p style="margin:0;color:#57534E;font-size:14px;line-height:1.6;">
      {{greeting}}
    </p>
  </div>

  <!-- Order summary -->
  <div style="margin:18px 24px 0;background:#ffffff;border:1px solid #F0E4D8;border-radius:16px;padding:16px;">
    <p style="margin:0 0 6px;color:#A16B3F;font-size:12px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;">{{what_you_ordered}}</p>
    <p style="margin:0;color:#44403C;font-size:13.5px;line-height:1.6;">{products_summary_with_quantity}</p>
    <div style="display:flex;justify-content:space-between;margin-top:12px;padding-top:12px;border-top:1px solid #F0E4D8;">
      <span style="color:#A8A29E;font-size:13px;">{{shipping}}</span>
      <span style="color:#44403C;font-size:13px;">{shipping_rate_name}</span>
    </div>
    <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
      <span style="color:#44403C;font-size:14px;font-weight:600;">{{total}}</span>
      <span style="color:#E07B39;font-size:18px;font-weight:700;">{order_total}</span>
    </div>
  </div>

  <!-- Delivery -->
  <div style="margin:14px 24px 0;background:#ffffff;border:1px solid #F0E4D8;border-radius:16px;padding:16px;">
    <p style="margin:0 0 6px;color:#A16B3F;font-size:12px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;">{{where_its_going}}</p>
    <p style="margin:0;color:#44403C;font-size:13.5px;line-height:1.7;">
      <strong>{full_name}</strong><br>
      {address}<br>
      {city}, {province}
    </p>
  </div>

  <!-- COD note -->
  <div style="margin:14px 24px 24px;background:#F2F8F1;border:1px solid #C8E2C4;border-radius:16px;padding:14px 16px;display:flex;align-items:center;gap:12px;text-align:left;">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;">
      <rect x="3" y="7" width="18" height="12" rx="2" stroke="#4D8B45" stroke-width="1.8"/>
      <circle cx="12" cy="13" r="2.6" stroke="#4D8B45" stroke-width="1.8"/>
      <path d="M6 7V5.5A1.5 1.5 0 017.5 4h9A1.5 1.5 0 0118 5.5V7" stroke="#4D8B45" stroke-width="1.8"/>
    </svg>
    <span style="color:#3F5E3B;font-size:13px;line-height:1.5;">
      {{cod_note}}
    </span>
  </div>
</div>`;

// The `call:start`/`call:end` markers wrap step 2. `buildConfirmationTemplate` drops the whole
// block when the toggle is off (2-step timeline) and removes just the markers when it is on.
// Step 1's connector line is outside the block, so it still reaches step 3 either way.
const DELIVERY_STEPS = `<div style="font-family:-apple-system,'Segoe UI',Roboto,Arial,sans-serif;max-width:480px;margin:0 auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 12px 40px rgba(49,46,129,0.18);">

  <!-- Header -->
  <div style="background:linear-gradient(135deg,#312E81 0%,#4338CA 60%,#6366F1 100%);padding:26px 24px 22px;text-align:center;">
    <h2 style="margin:0;color:#ffffff;font-size:21px;font-weight:700;">{{order_confirmed}}</h2>
    <p style="margin:8px 0 0;color:#C7D2FE;font-size:13.5px;">{{subheader}}</p>
  </div>

  <!-- Steps -->
  <div style="padding:22px 24px 4px;">
    <!-- Step 1 -->
    <div style="display:flex;gap:14px;">
      <div style="display:flex;flex-direction:column;align-items:center;">
        <div style="width:32px;height:32px;background:#4338CA;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 12.5L9.5 18L20 6.5" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div style="width:2px;flex-grow:1;background:#E0E7FF;margin:4px 0;"></div>
      </div>
      <div style="padding-bottom:18px;">
        <p style="margin:0;color:#111827;font-size:14px;font-weight:700;">{{step1_title}}</p>
        <p style="margin:4px 0 0;color:#6B7280;font-size:13px;line-height:1.6;">{products_summary_with_quantity}</p>
      </div>
    </div>
<!--call:start-->
    <!-- Step 2 -->
    <div style="display:flex;gap:14px;">
      <div style="display:flex;flex-direction:column;align-items:center;">
        <div style="width:32px;height:32px;background:#EEF2FF;border:2px solid #4338CA;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 4h4l2 5-2.5 1.5a12 12 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z" stroke="#4338CA" stroke-width="1.8" stroke-linejoin="round"/>
          </svg>
        </div>
        <div style="width:2px;flex-grow:1;background:#E0E7FF;margin:4px 0;"></div>
      </div>
      <div style="padding-bottom:18px;">
        <p style="margin:0;color:#111827;font-size:14px;font-weight:700;">{{step2_title}}</p>
        <p style="margin:4px 0 0;color:#6B7280;font-size:13px;line-height:1.6;">{{step2_body}}</p>
      </div>
    </div>
<!--call:end-->
    <!-- Step 3 -->
    <div style="display:flex;gap:14px;">
      <div style="display:flex;flex-direction:column;align-items:center;">
        <div style="width:32px;height:32px;background:#EEF2FF;border:2px solid #4338CA;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="7" width="14" height="10" rx="2" stroke="#4338CA" stroke-width="1.8"/>
            <path d="M16 10h3.2L22 13v4h-6" stroke="#4338CA" stroke-width="1.8" stroke-linejoin="round"/>
            <circle cx="7" cy="17" r="1.8" fill="#ffffff" stroke="#4338CA" stroke-width="1.8"/>
            <circle cx="18" cy="17" r="1.8" fill="#ffffff" stroke="#4338CA" stroke-width="1.8"/>
          </svg>
        </div>
      </div>
      <div style="padding-bottom:6px;">
        <p style="margin:0;color:#111827;font-size:14px;font-weight:700;">{{step3_title}}</p>
        <p style="margin:4px 0 0;color:#6B7280;font-size:13px;line-height:1.6;">
          {{step3_body}}
        </p>
      </div>
    </div>
  </div>

  <!-- Footer strip -->
  <div style="background:#EEF2FF;padding:12px 24px;text-align:center;margin-top:16px;">
    <span style="color:#4338CA;font-size:12.5px;">{{footer}}</span>
  </div>
</div>`;

const TEMPLATES: Record<ConfirmationTemplateId, ConfirmationTemplate> = {
  '1-clean-minimal': {
    html: CLEAN_MINIMAL,
    callKey: 'greeting',
    strings: {
      en: {
        greeting_call_on:
          'Thank you, <strong>{first_name}</strong>. We will call you at <strong>{phone}</strong> to confirm your delivery.',
        greeting_call_off:
          'Thank you, <strong>{first_name}</strong>. Your order is confirmed and will be shipped to your address.',
        your_order: 'Your order',
        delivery_address: 'Delivery address',
        cod_note: 'Pay <strong>{order_total}</strong> in cash when your order arrives.',
      },
      es: {
        greeting_call_on:
          'Gracias, <strong>{first_name}</strong>. Te llamaremos al <strong>{phone}</strong> para confirmar la entrega.',
        greeting_call_off:
          'Gracias, <strong>{first_name}</strong>. Tu pedido está confirmado y será enviado a tu dirección.',
        your_order: 'Tu pedido',
        delivery_address: 'Dirección de entrega',
        cod_note: 'Paga <strong>{order_total}</strong> en efectivo cuando llegue tu pedido.',
      },
      'pt-BR': {
        greeting_call_on:
          'Obrigado, <strong>{first_name}</strong>. Vamos ligar para <strong>{phone}</strong> para confirmar a entrega.',
        greeting_call_off:
          'Obrigado, <strong>{first_name}</strong>. Seu pedido está confirmado e será enviado para o seu endereço.',
        your_order: 'Seu pedido',
        delivery_address: 'Endereço de entrega',
        cod_note: 'Pague <strong>{order_total}</strong> em dinheiro quando seu pedido chegar.',
      },
    },
  },

  '2-bold-banner': {
    html: BOLD_BANNER,
    callKey: 'greeting',
    strings: {
      en: {
        header: 'ORDER CONFIRMED',
        greeting_call_on:
          'Thank you, <strong>{first_name}</strong>. We will call you at <strong>{phone}</strong> to arrange the delivery.',
        greeting_call_off:
          'Thank you, <strong>{first_name}</strong>. Your order is confirmed and will be shipped to your address.',
        your_order: 'Your order',
        shipping_line: 'Shipping: {shipping_rate_name}',
        total_to_pay: 'Total to pay',
        delivery_details: 'Delivery details',
        cod_note:
          '<strong>Cash on delivery:</strong> pay <strong>{order_total}</strong> in cash when your order arrives at your door.',
        footer: 'Thank you for shopping with us',
      },
      es: {
        header: 'PEDIDO CONFIRMADO',
        greeting_call_on:
          'Gracias, <strong>{first_name}</strong>. Te llamaremos al <strong>{phone}</strong> para coordinar la entrega.',
        greeting_call_off:
          'Gracias, <strong>{first_name}</strong>. Tu pedido está confirmado y será enviado a tu dirección.',
        your_order: 'Tu pedido',
        shipping_line: 'Envío: {shipping_rate_name}',
        total_to_pay: 'Total a pagar',
        delivery_details: 'Datos de entrega',
        cod_note:
          '<strong>Pago contra entrega:</strong> paga <strong>{order_total}</strong> en efectivo cuando recibas tu pedido en la puerta de tu casa.',
        footer: 'Gracias por tu compra',
      },
      'pt-BR': {
        header: 'PEDIDO CONFIRMADO',
        greeting_call_on:
          'Obrigado, <strong>{first_name}</strong>. Vamos ligar para <strong>{phone}</strong> para combinar a entrega.',
        greeting_call_off:
          'Obrigado, <strong>{first_name}</strong>. Seu pedido está confirmado e será enviado para o seu endereço.',
        your_order: 'Seu pedido',
        shipping_line: 'Frete: {shipping_rate_name}',
        total_to_pay: 'Total a pagar',
        delivery_details: 'Dados de entrega',
        cod_note:
          '<strong>Pagamento na entrega:</strong> pague <strong>{order_total}</strong> em dinheiro quando receber seu pedido na sua porta.',
        footer: 'Obrigado pela sua compra',
      },
    },
  },

  '3-receipt': {
    html: RECEIPT,
    callKey: 'footer',
    strings: {
      en: {
        header: 'Order Confirmed',
        order_no: 'No. {order_number}',
        items: 'Items',
        deliver_to: 'Deliver to',
        tel: 'Tel: {phone}',
        cod_bar: 'CASH ON DELIVERY · PAY <strong>{order_total}</strong> AT YOUR DOOR',
        footer_call_on: 'Thank you, {first_name}. We will call you to confirm.',
        footer_call_off: 'Thank you, {first_name}. Your order will arrive soon.',
      },
      es: {
        header: 'Pedido Confirmado',
        order_no: 'N.º {order_number}',
        items: 'Productos',
        deliver_to: 'Entregar a',
        tel: 'Tel: {phone}',
        cod_bar: 'PAGO CONTRA ENTREGA · PAGA <strong>{order_total}</strong> AL RECIBIR',
        footer_call_on: 'Gracias, {first_name}. Te llamaremos para confirmar.',
        footer_call_off: 'Gracias, {first_name}. Tu pedido llegará pronto.',
      },
      'pt-BR': {
        header: 'Pedido Confirmado',
        order_no: 'N.º {order_number}',
        items: 'Produtos',
        deliver_to: 'Entregar para',
        tel: 'Tel: {phone}',
        cod_bar: 'PAGAMENTO NA ENTREGA · PAGUE <strong>{order_total}</strong> AO RECEBER',
        footer_call_on: 'Obrigado, {first_name}. Vamos ligar para confirmar.',
        footer_call_off: 'Obrigado, {first_name}. Seu pedido chegará em breve.',
      },
    },
  },

  '4-soft-friendly': {
    html: SOFT_FRIENDLY,
    callKey: 'greeting',
    strings: {
      en: {
        header: 'Thank you, {first_name}!',
        subheader: 'Your order {order_number} is confirmed.',
        greeting_call_on:
          'We will give you a quick call at <strong>{phone}</strong> to confirm everything before shipping.',
        greeting_call_off: 'We are preparing your package and will ship it to your address.',
        what_you_ordered: 'What you ordered',
        where_its_going: "Where it's going",
        cod_note:
          'No payment needed now. Pay <strong>{order_total}</strong> in cash when your package arrives.',
      },
      es: {
        header: '¡Gracias, {first_name}!',
        subheader: 'Tu pedido {order_number} está confirmado.',
        greeting_call_on:
          'Te haremos una breve llamada al <strong>{phone}</strong> para confirmar todo antes del envío.',
        greeting_call_off: 'Estamos preparando tu paquete y lo enviaremos a tu dirección.',
        what_you_ordered: 'Lo que pediste',
        where_its_going: 'Dónde lo enviaremos',
        cod_note:
          'No pagas nada ahora. Paga <strong>{order_total}</strong> en efectivo cuando llegue tu paquete.',
      },
      'pt-BR': {
        header: 'Obrigado, {first_name}!',
        subheader: 'Seu pedido {order_number} está confirmado.',
        greeting_call_on:
          'Vamos fazer uma ligação rápida para <strong>{phone}</strong> para confirmar tudo antes do envio.',
        greeting_call_off: 'Estamos preparando seu pacote e vamos enviá-lo para o seu endereço.',
        what_you_ordered: 'O que você pediu',
        where_its_going: 'Para onde vamos enviar',
        cod_note:
          'Você não paga nada agora. Pague <strong>{order_total}</strong> em dinheiro quando seu pacote chegar.',
      },
    },
  },

  '5-delivery-steps': {
    html: DELIVERY_STEPS,
    strings: {
      en: {
        subheader:
          'Order {order_number} · Total <strong style="color:#ffffff;">{order_total}</strong>',
        step1_title: 'Order received',
        step2_title: 'We call to confirm',
        step2_body:
          'Expect our call at <strong style="color:#111827;">{phone}</strong>, {first_name}.',
        step3_title: 'Pay on delivery',
        step3_body:
          'Pay <strong style="color:#111827;">{order_total}</strong> in cash at<br>\n          {address}, {city}, {province}',
        footer: 'Shipping via <strong>{shipping_rate_name}</strong> · Thank you, {full_name}',
      },
      es: {
        subheader:
          'Pedido {order_number} · Total <strong style="color:#ffffff;">{order_total}</strong>',
        step1_title: 'Pedido recibido',
        step2_title: 'Te llamamos para confirmar',
        step2_body:
          'Espera nuestra llamada al <strong style="color:#111827;">{phone}</strong>, {first_name}.',
        step3_title: 'Paga al recibir',
        step3_body:
          'Paga <strong style="color:#111827;">{order_total}</strong> en efectivo en<br>\n          {address}, {city}, {province}',
        footer: 'Envío: <strong>{shipping_rate_name}</strong> · Gracias, {full_name}',
      },
      'pt-BR': {
        subheader:
          'Pedido {order_number} · Total <strong style="color:#ffffff;">{order_total}</strong>',
        step1_title: 'Pedido recebido',
        step2_title: 'Ligamos para confirmar',
        step2_body:
          'Aguarde nossa ligação no <strong style="color:#111827;">{phone}</strong>, {first_name}.',
        step3_title: 'Pague na entrega',
        step3_body:
          'Pague <strong style="color:#111827;">{order_total}</strong> em dinheiro em<br>\n          {address}, {city}, {province}',
        footer: 'Frete: <strong>{shipping_rate_name}</strong> · Obrigado, {full_name}',
      },
    },
  },
};

/** Display order in the picker. */
export const CONFIRMATION_TEMPLATE_IDS: ConfirmationTemplateId[] = [
  '1-clean-minimal',
  '2-bold-banner',
  '3-receipt',
  '4-soft-friendly',
  '5-delivery-steps',
];

/**
 * The template locale for a store. `main_language` is Nuvemshop's own code (`es`, `pt`, `en`);
 * anything else — or a store whose install-time lookup failed — falls back to English.
 */
export function templateLocale(storeLanguage: string | null): TemplateLocale {
  const base = (storeLanguage ?? '').slice(0, 2).toLowerCase();
  if (base === 'es') return 'es';
  if (base === 'pt') return 'pt-BR';
  return 'en';
}

/** Finished HTML for the editor: localized copy, call-to-confirm variant resolved. */
export function buildConfirmationTemplate(
  id: ConfirmationTemplateId,
  locale: TemplateLocale,
  callToConfirm: boolean,
): string {
  const template = TEMPLATES[id];
  const strings: Record<string, string> = { ...SHARED[locale], ...template.strings[locale] };
  if (template.callKey) {
    strings[template.callKey] =
      strings[`${template.callKey}_call_${callToConfirm ? 'on' : 'off'}`] ?? '';
  }
  return template.html
    .replace(
      callToConfirm
        ? /[ \t]*<!--call:(start|end)-->\n/g
        : /[ \t]*<!--call:start-->[\s\S]*?<!--call:end-->\n/g,
      '',
    )
    .replace(/\{\{(\w+)\}\}/g, (placeholder, key: string) => strings[key] ?? placeholder);
}

/** The three picker choices that produce one exact template output. */
export interface ConfirmationRecipe {
  id: ConfirmationTemplateId;
  locale: TemplateLocale;
  callToConfirm: boolean;
}

/**
 * The full set of choices that produced `html`, or null when it is empty or the merchant has
 * written/edited it.
 *
 * Matched by comparing against every build output (5 templates x 3 locales x 2 toggle states)
 * rather than stamping an id into the inserted HTML: the merchant owns that HTML and can edit or
 * delete any part of it, so a marker would be both a lie waiting to happen and implementation
 * detail sitting in their content. An exact match is the only claim worth making — the moment
 * they change a character the answer is honestly "custom".
 *
 * Returning all three (not just the id) is what lets the picker reopen on the merchant's actual
 * template instead of resetting to the first one in the list.
 */
export function detectConfirmationRecipe(html: string): ConfirmationRecipe | null {
  const trimmed = html.trim();
  if (!trimmed) return null;
  for (const id of CONFIRMATION_TEMPLATE_IDS) {
    for (const locale of TEMPLATE_LOCALES) {
      for (const callToConfirm of [true, false]) {
        if (buildConfirmationTemplate(id, locale, callToConfirm).trim() === trimmed) {
          return { id, locale, callToConfirm };
        }
      }
    }
  }
  return null;
}

/** Which template produced `html`, or null. See `detectConfirmationRecipe`. */
export function detectConfirmationTemplate(html: string): ConfirmationTemplateId | null {
  return detectConfirmationRecipe(html)?.id ?? null;
}

/**
 * The picker's sixth choice, which is a mode rather than a design: it produces no HTML of its own
 * and is the only one that opens the raw editor. Kept as a distinct value instead of a sixth
 * template id so `buildConfirmationTemplate` cannot be handed it — there is nothing to build.
 */
export const CUSTOM_TEMPLATE_CHOICE = 'custom';

/** What the design picker can hold: one of the five templates, or the merchant's own HTML. */
export type TemplateChoice = ConfirmationTemplateId | typeof CUSTOM_TEMPLATE_CHOICE;
